import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../Context/AuthContext";
import { APPRoutes } from "../../Utils/Route";
import { isSuperAdminUser } from "../../Utils/Permissions";
import { getUserById, saveUser } from "../../Services/UserService";
import { getRolesName } from "../../Services/RolesService";
import { getAllClientNames } from "../../Services/ClientService";
import { getAllProviderNames, getProviderById } from "../../Services/ProviderService";
import { getAllStaffNames, getStaffById } from "../../Services/StaffService";
import SearchableDropdown from "../../Components/Subscription/SearchableDropdown";
import CustomPhoneInput from "../../Components/Common/CustomPhoneInput";
import DotLottie from "../../Components/Common/DotLottie";

const STATUS_OPTIONS = [
	{ id: 1, label: "Active" },
	{ id: 2, label: "Inactive" },
];

// statuses.id values, status_type 14 = "Internal User Type", 15 = "External User Type"
const USER_TYPE_BILLING_MEMBER = 78;
const USER_TYPE_CREDENTIALING_MEMBER = 79;
const USER_TYPE_STAFF = 80;
const USER_TYPE_PROVIDER = 81;
const USER_TYPE_GENERAL = 82;

const USER_CATEGORY_OPTIONS = [
	{ value: "external", label: "External" },
	{ value: "internal", label: "Internal" },
];

const USER_TYPE_OPTIONS_BY_CATEGORY = {
	external: [
		{ value: USER_TYPE_PROVIDER, label: "Provider" },
		{ value: USER_TYPE_STAFF, label: "Staff" },
		{ value: USER_TYPE_GENERAL, label: "General" },
	],
	internal: [
		{ value: USER_TYPE_CREDENTIALING_MEMBER, label: "Credentialing Member" },
		{ value: USER_TYPE_BILLING_MEMBER, label: "Billing Member" },
	],
};

const USER_TYPE_TO_CATEGORY = {
	[USER_TYPE_PROVIDER]: "external",
	[USER_TYPE_STAFF]: "external",
	[USER_TYPE_GENERAL]: "external",
	[USER_TYPE_CREDENTIALING_MEMBER]: "internal",
	[USER_TYPE_BILLING_MEMBER]: "internal",
};

const emptyForm = {
	id: null,
	firstName: "",
	lastName: "",
	emailAddress: "",
	phone: "",
	userCategory: "",
	userType: "",
	providerId: "",
	staffId: "",
	roleId: "",
	accountId: null,
	statusId: 1,
};

const validationSchema = Yup.object({
	firstName: Yup.string().trim().required("First Name is required"),
	lastName: Yup.string().trim().required("Last Name is required"),
	emailAddress: Yup.string().trim().email("Invalid email").required("Email is required"),
	phone: Yup.string().trim().nullable(),
	userCategory: Yup.string()
		.nullable()
		.oneOf(["external", "internal", "", null], "Invalid User Category"),
	userType: Yup.number()
		.transform((value, originalValue) => (originalValue === "" || originalValue == null ? undefined : value))
		.when("userCategory", {
			is: (value) => !!value,
			then: (schema) =>
				schema
					.required("User Type is required")
					.oneOf([USER_TYPE_BILLING_MEMBER, USER_TYPE_CREDENTIALING_MEMBER, USER_TYPE_STAFF, USER_TYPE_PROVIDER, USER_TYPE_GENERAL], "User Type is required"),
			otherwise: (schema) => schema.notRequired(),
		}),
	providerId: Yup.string().when("userType", {
		is: USER_TYPE_PROVIDER,
		then: (schema) => schema.required("Provider is required"),
		otherwise: (schema) => schema.notRequired(),
	}),
	staffId: Yup.string().when("userType", {
		is: USER_TYPE_STAFF,
		then: (schema) => schema.required("Staff is required"),
		otherwise: (schema) => schema.notRequired(),
	}),
	roleId: Yup.string().required("Role is required"),
	statusId: Yup.number().required("Status is required"),
});


export default function UsersForm() {
	const navigate = useNavigate();
	const { id } = useParams();
	const isEdit = !!id;
	const { token, decodedToken } = useAuth();
	const isSuperAdmin = isSuperAdminUser(decodedToken);
	const accountId = decodedToken?.account_id;

	const [loading, setLoading] = useState(isEdit);
	const [roles, setRoles] = useState([]);
	const [rolesLoading, setRolesLoading] = useState(false);
	const [clients, setClients] = useState([]);
	const [clientsLoading, setClientsLoading] = useState(false);
	const [providers, setProviders] = useState([]);
	const [providersLoading, setProvidersLoading] = useState(false);
	const [staffList, setStaffList] = useState([]);
	const [staffLoading, setStaffLoading] = useState(false);

	function mapApiToForm(userData) {
		const rawUserType = userData?.UserType ?? userData?.user_type;
		const userType = rawUserType === null || rawUserType === undefined || rawUserType === "" ? "" : Number(rawUserType);
		return {
			id: userData?.Id ?? userData?.id ?? null,
			firstName: userData?.FirstName ?? userData?.first_name ?? "",
			lastName: userData?.LastName ?? userData?.last_name ?? "",
			emailAddress: userData?.EmailAddress ?? userData?.email_address ?? "",
			phone: userData?.Phone ?? userData?.phone ?? "",
			userCategory: userType !== "" ? (USER_TYPE_TO_CATEGORY[userType] || "") : "",
			userType,
			providerId:
				userData?.ProviderId ??
				userData?.provider_id ??
				userData?.providerId ??
				userData?.Provider?.Id ??
				userData?.Provider?.ProviderId ??
				"",
			staffId:
				userData?.StaffId ??
				userData?.staff_id ??
				userData?.staffId ??
				userData?.Staff?.Id ??
				"",
			roleId:
				userData?.Role?.Id ??
				userData?.role_id ??
				userData?.RoleId ??
				"",
			accountId: isSuperAdmin ? userData?.ClientId ?? userData?.account_id ?? null : accountId,
			statusId: userData?.StatusId ?? userData?.status_id ?? 1,
		};
	}

	function buildPayload(values) {
		const resolvedAccountId = isSuperAdmin ? values.accountId || null : accountId || null;
		const userType = values.userType !== "" && values.userType != null ? Number(values.userType) : null;
		return {
			id: values.id ? String(values.id) : null,
			first_name: values.firstName?.trim() || "",
			last_name: values.lastName?.trim() || "",
			email_address: values.emailAddress?.trim() || "",
			phone: values.phone?.trim() || null,
			user_type: userType,
			provider_id: userType === USER_TYPE_PROVIDER ? values.providerId || null : null,
			staff_id: userType === USER_TYPE_STAFF ? values.staffId || null : null,
			role_id: values.roleId || null,
			account_id: resolvedAccountId,
			status_id: Number(values.statusId) || 1,
			type: values.id ? "update" : "create",
		};
	}

	const formik = useFormik({
		initialValues: useMemo(() => emptyForm, []),
		enableReinitialize: false,
		validationSchema,
		onSubmit: async (values) => {
			const errors = await formik.validateForm();
			if (Object.keys(errors).length > 0) {
				formik.setTouched({
					firstName: true,
					lastName: true,
					emailAddress: true,
					phone: true,
					roleId: true,
					statusId: true,
					userCategory: true,
					userType: true,
					providerId: true,
					staffId: true,
				}, false);
				toast.error("Please fix the highlighted fields before saving.");
				return;
			}

			try {
				const payload = buildPayload(values);
				const response = await saveUser(payload, token);
				if (response?.is_success) {
					toast.success(response?.message || "User saved successfully");
					navigate(APPRoutes.USERS_VIEW.replace(":id", response?.data?.Id || response?.data?.id || values.id));
				} else if (response?.is_success === false && response?.status_code === 500) {
					toast.error("Internal Server Error. Please Contact Support Team.");
				} else {
					toast.error(response?.message || "Failed to save user");
				}
			} catch (error) {
				console.error(error);
				toast.error(error.message || "Failed to save user");
			}
		},
	});

	const loadUser = async () => {
		if (!isEdit) return;
		try {
			setLoading(true);
			const response = await getUserById(id, token);
			if (response?.is_success) {
				const userData = response?.data ?? response;
				formik.setValues(mapApiToForm(userData));
			} else if (response?.is_success === false && response?.status_code === 500) {
				toast.error('Internal Server Error. Please Contact Support Team.');
				formik.setValues(emptyForm);
			} else {
				toast.error(response?.message || "Failed to load user");
				formik.setValues(emptyForm);
			}
		} catch (error) {
			console.error(error);
			toast.error(error.message || "Failed to load user");
		} finally {
			setLoading(false);
		}
	};

	const loadRoles = async () => {
		try {
			setRolesLoading(true);
			const response = await getRolesName(token);
			if (response?.is_success) {
				setRoles(response?.data ?? response);
			} else if (response?.is_success === false && response?.status_code === 500) {
				toast.error('Internal Server Error. Please Contact Support Team.');
				setRoles([]);
			} else {
				toast.error(response?.message || "Failed to load roles");
				setRoles([]);
			}
		} catch (error) {
			console.error(error);
			toast.error(error.message || "Failed to load roles");
		} finally {
			setRolesLoading(false);
		}
	};


	const loadClients = async () => {
		if (!isSuperAdmin) return;
		try {
			setClientsLoading(true);
			const response = await getAllClientNames(token);
			if (response?.is_success) {
				const data = Array.isArray(response?.data) ? response.data : [];
				setClients(data);
			} else if (response?.is_success === false && response?.status_code === 500) {
				toast.error('Internal Server Error. Please Contact Support Team.');
				setClients([]);
			} else {
				toast.error(response?.message || "Failed to load clients");
				setClients([]);
			}
		} catch (error) {
			console.error(error);
			toast.error(error.message || "Failed to load clients");
			setClients([]);
		} finally {
			setClientsLoading(false);
		}
	};

	const loadProviders = async (clientId) => {
		try {
			setProvidersLoading(true);
			const response = await getAllProviderNames(token, clientId || undefined);
			if (response?.is_success === false && response?.status_code === 500) {
				toast.error('Internal Server Error. Please Contact Support Team.');
				setProviders([]);
				return;
			}

			const items =
				response?.data?.Providers ||
				response?.data?.providers ||
				response?.data ||
				response?.providers ||
				response;

			setProviders(Array.isArray(items) ? items : []);
		} catch (error) {
			console.error(error);
			toast.error(error.message || "Failed to load providers");
			setProviders([]);
		} finally {
			setProvidersLoading(false);
		}
	};

	const loadStaff = async (clientId) => {
		try {
			setStaffLoading(true);
			const response = await getAllStaffNames(token, clientId || undefined);
			if (response?.is_success === false && response?.status_code === 500) {
				toast.error('Internal Server Error. Please Contact Support Team.');
				setStaffList([]);
				return;
			}

			const items =
				response?.data?.Staffs ||
				response?.data?.staffs ||
				response?.data?.staff ||
				response?.data ||
				response?.Staffs ||
				response?.staffs ||
				response?.staff ||
				response;

			setStaffList(Array.isArray(items) ? items : []);
		} catch (error) {
			console.error(error);
			toast.error(error.message || "Failed to load staff");
			setStaffList([]);
		} finally {
			setStaffLoading(false);
		}
	};

	const handleProviderChange = async (value) => {
		formik.setFieldValue("providerId", value || "");
		if (!value) return;

		const selectedProvider = providers.find(
			(p) => String(p?.id ?? p?.Id ?? p?.ProviderId ?? p?.provider_id) === String(value)
		);

		let firstName = selectedProvider?.first_name ?? selectedProvider?.FirstName ?? "";
		let lastName = selectedProvider?.last_name ?? selectedProvider?.LastName ?? "";
		let email = selectedProvider?.email ?? selectedProvider?.work_email ?? selectedProvider?.personal_email ?? selectedProvider?.Email ?? "";
		let phone = selectedProvider?.phone ?? selectedProvider?.mobile_phone ?? selectedProvider?.home_phone ?? selectedProvider?.phone_number ?? selectedProvider?.Phone ?? "";

		// If name or phone details are missing in the list item, fetch full provider details
		if (!firstName || !lastName || !phone) {
			try {
				const res = await getProviderById(token, value);
				const pData = res?.data ?? res;
				if (pData) {
					if (!firstName) firstName = pData?.first_name ?? pData?.FirstName ?? "";
					if (!lastName) lastName = pData?.last_name ?? pData?.LastName ?? "";
					if (!email) email = pData?.work_email ?? pData?.personal_email ?? pData?.email ?? pData?.Email ?? "";
					if (!phone) phone = pData?.mobile_phone ?? pData?.home_phone ?? pData?.phone ?? pData?.phone_number ?? pData?.Phone ?? "";
				}
			} catch (e) {
				console.error("Error fetching provider details for auto-prefill:", e);
			}
		}

		if (firstName !== undefined) formik.setFieldValue("firstName", firstName || "");
		if (lastName !== undefined) formik.setFieldValue("lastName", lastName || "");
		if (email !== undefined) formik.setFieldValue("emailAddress", email || "");
		if (phone !== undefined) formik.setFieldValue("phone", phone || "");
	};

	const handleStaffChange = async (value) => {
		formik.setFieldValue("staffId", value || "");
		if (!value) return;

		const selectedStaff = staffList.find(
			(s) => String(s?.id ?? s?.Id ?? s?.StaffId ?? s?.staff_id) === String(value)
		);

		let firstName = selectedStaff?.first_name ?? selectedStaff?.FirstName ?? "";
		let lastName = selectedStaff?.last_name ?? selectedStaff?.LastName ?? "";
		let email = selectedStaff?.email ?? selectedStaff?.Email ?? "";
		let phone = selectedStaff?.phone ?? selectedStaff?.phone_number ?? selectedStaff?.Phone ?? "";

		// If name or phone details are missing in the list item, fetch full staff details
		if (!firstName || !lastName || !phone) {
			try {
				const res = await getStaffById(value, token);
				const sData = res?.data ?? res;
				if (sData) {
					if (!firstName) firstName = sData?.first_name ?? sData?.FirstName ?? "";
					if (!lastName) lastName = sData?.last_name ?? sData?.LastName ?? "";
					if (!email) email = sData?.email ?? sData?.Email ?? "";
					if (!phone) phone = sData?.phone_number ?? sData?.phone ?? sData?.Phone ?? "";
				}
			} catch (e) {
				console.error("Error fetching staff details for auto-prefill:", e);
			}
		}

		if (firstName !== undefined) formik.setFieldValue("firstName", firstName || "");
		if (lastName !== undefined) formik.setFieldValue("lastName", lastName || "");
		if (email !== undefined) formik.setFieldValue("emailAddress", email || "");
		if (phone !== undefined) formik.setFieldValue("phone", phone || "");
	};

	const effectiveClientId = isSuperAdmin ? (formik.values.accountId || null) : (accountId || null);

	React.useEffect(() => {
		if (!token) return;
		loadUser();
		loadRoles();
		if (isSuperAdmin) loadClients();
	}, [id, isEdit, token, isSuperAdmin]);

	React.useEffect(() => {
		if (!token) return;
		loadProviders(effectiveClientId);
		loadStaff(effectiveClientId);
	}, [token, effectiveClientId]);

	const renderAsterisk = () => <span className="text-danger">*</span>;

	if (loading) {
		return (
			<div className="d-flex justify-content-center align-items-center min-vh-100">
				<DotLottie />
			</div>
		);
	}

	return (
		<div className="container-fluid p-0">
			<div className="rounded-4 bg-white p-2 p-md-4 shadow-lg border-0">
				<div className="d-flex align-items-center justify-content-between mb-4">
					<div className="d-flex align-items-center gap-3">
						<button
							type="button"
							className="btn btn-light border rounded-circle shadow-sm"
							onClick={() => navigate(-1)}
							style={{ width: 40, height: 40 }}
						>
							&larr;
						</button>
						<div>
							<h3 className="fw-bold mb-0 hc-text-primary">
								{isEdit ? "Edit User" : "Create User"}
							</h3>
							<small className="text-muted">
								Define user details & assign role
							</small>
						</div>
					</div>
				</div>

				<form onSubmit={formik.handleSubmit}>
					<div className="mb-4 p-4 rounded-4 border bg-light-subtle">
						<h6 className="fw-bold hc-text-primary mb-3">Basic Information</h6>
						<div className="row g-3">
							<div className="col-md-6">
								<label className="form-label fw-semibold">
									First Name {renderAsterisk()}
								</label>
								<input
									className={`form-control rounded-3 shadow-sm ${formik.touched.firstName && formik.errors.firstName ? "is-invalid" : ""}`}
									name="firstName"
									placeholder="First Name"
									{...formik.getFieldProps("firstName")}
								/>
								<div className="invalid-feedback">{formik.errors.firstName}</div>
							</div>
							<div className="col-md-6">
								<label className="form-label fw-semibold">
									Last Name {renderAsterisk()}
								</label>
								<input
									className={`form-control rounded-3 shadow-sm ${formik.touched.lastName && formik.errors.lastName ? "is-invalid" : ""}`}
									name="lastName"
									placeholder="Last Name"
									{...formik.getFieldProps("lastName")}
								/>
								<div className="invalid-feedback">{formik.errors.lastName}</div>
							</div>
							<div className="col-md-6">
								<label className="form-label fw-semibold">
									Email Address {renderAsterisk()}
								</label>
								<input
									className={`form-control rounded-3 shadow-sm ${formik.touched.emailAddress && formik.errors.emailAddress ? "is-invalid" : ""}`}
									name="emailAddress"
									placeholder="user@example.com"
									{...formik.getFieldProps("emailAddress")}
								/>
								<div className="invalid-feedback">{formik.errors.emailAddress}</div>
							</div>
							<div className="col-md-6">
								<label className="form-label fw-semibold">Phone</label>

								<CustomPhoneInput
									formik={formik}
									name="phone"
									placeholder="+91 9876543210"
								/>

								{/* <input
									className={`form-control rounded-3 shadow-sm ${formik.touched.phone && formik.errors.phone ? "is-invalid" : ""}`}
									name="phone"
									placeholder="9876543210"
									{...formik.getFieldProps("phone")}
								/> */}
								<div className="invalid-feedback">{formik.errors.phone}</div>
							</div>
							<div className="col-md-6">
								<SearchableDropdown
									label="Status"
									placeholder="Select Status"
									options={STATUS_OPTIONS.map((item) => ({ value: item.id, label: item.label }))}
									value={formik.values.statusId}
									onChange={(value) => formik.setFieldValue("statusId", value !== '' ? Number(value) : '')}
									showSearch={false}
									required
									error={formik.touched.statusId && formik.errors.statusId ? formik.errors.statusId : ''}
								/>
							</div>
							<div className="col-md-6">
								<SearchableDropdown
									label="Role"
									placeholder="Select Role"
									options={roles.map((role) => ({ value: role.Id, label: role.Name }))}
									value={formik.values.roleId ?? ""}
									onChange={(value) => {
										formik.setFieldValue("roleId", value || "");
										const selectedRole = roles.find((role) => String(role.Id) === String(value));
										if (selectedRole?.AccountId) {
											formik.setFieldValue("accountId", selectedRole.AccountId);
										}
									}}
									disabled={rolesLoading}
									loading={rolesLoading}
									required
									emptyMessage="No roles found"
								/>
								<div className="invalid-feedback d-block">{formik.errors.roleId}</div>
							</div>
							{isSuperAdmin && (
								<div className="col-md-6">
									<SearchableDropdown
										label="Client"
										placeholder="Global User"
										options={clients.map((client) => ({
											value: client.Id ?? client.id,
											label: client.OrganizationName || client.Name || client.name || client.ClientName || "Client",
										}))}
										value={formik.values.accountId ?? ""}
										onChange={(value) => {
											formik.setFieldValue("accountId", value || null);
											formik.setFieldValue("providerId", "");
											formik.setFieldValue("staffId", "");
										}}
										disabled={clientsLoading}
										loading={clientsLoading}
										required={false}
										emptyMessage="No clients found"
									/>

								</div>
							)}
							<>
								<div className="col-md-6">
									<SearchableDropdown
											label="User Category"
											placeholder="Select User Category (optional)"
											options={USER_CATEGORY_OPTIONS}
											value={formik.values.userCategory ?? ""}
											onChange={(value) => {
												const nextCategory = value || "";
												formik.setFieldValue("userCategory", nextCategory);
												formik.setFieldValue("userType", "");
												formik.setFieldValue("providerId", "");
												formik.setFieldValue("staffId", "");
											}}
											showSearch={false}
											required={false}
											error={formik.touched.userCategory && formik.errors.userCategory ? formik.errors.userCategory : ''}
										/>

								</div>
								<div className="col-md-6">
									<SearchableDropdown
										label="User Type"
										placeholder="Select User Type"
										options={USER_TYPE_OPTIONS_BY_CATEGORY[formik.values.userCategory] || []}
										value={formik.values.userType ?? ""}
										onChange={(value) => {
											const nextType = value !== '' && value != null ? Number(value) : "";
											formik.setFieldValue("userType", nextType);
											if (nextType !== USER_TYPE_PROVIDER) formik.setFieldValue("providerId", "");
											if (nextType !== USER_TYPE_STAFF) formik.setFieldValue("staffId", "");
										}}
										showSearch={false}
										disabled={!formik.values.userCategory}
										required={!!formik.values.userCategory}
										error={formik.touched.userType && formik.errors.userType ? formik.errors.userType : ''}
									/>
								</div>
								{formik.values.userType === USER_TYPE_PROVIDER && (
									<div className="col-md-6">
										<SearchableDropdown
											label="Provider"
											placeholder="Select Provider"
											options={providers.map((provider) => ({
												value: provider?.id ?? provider?.Id ?? provider?.ProviderId ?? provider?.provider_id ?? "",
												label: (provider?.name || provider?.ProviderName || `${provider?.FirstName || provider?.first_name || ""} ${provider?.LastName || provider?.last_name || ""}`.trim() || "Provider"),
											}))}
											value={formik.values.providerId ?? ""}
											onChange={(value) => handleProviderChange(value)}
											disabled={providersLoading}
											loading={providersLoading}
											required
											emptyMessage="No providers found for this client"
										/>
										<div className="invalid-feedback d-block">{formik.errors.providerId}</div>
									</div>
								)}
								{formik.values.userType === USER_TYPE_STAFF && (
									<div className="col-md-6">
										<SearchableDropdown
											label="Staff"
											placeholder="Select Staff"
											options={staffList.map((staff) => ({
												value: staff?.id ?? staff?.Id ?? staff?.StaffId ?? staff?.staff_id ?? "",
												label: (staff?.name || staff?.StaffName || `${staff?.FirstName || staff?.first_name || ""} ${staff?.LastName || staff?.last_name || ""}`.trim() || "Staff"),
											}))}
											value={formik.values.staffId ?? ""}
											onChange={(value) => handleStaffChange(value)}
											disabled={staffLoading}
											loading={staffLoading}
											required
											emptyMessage="No staff found for this client"
										/>
										<div className="invalid-feedback d-block">{formik.errors.staffId}</div>
									</div>
								)}
							</>
						</div>
					</div>
					<div className="text-end mt-4">
						<button
							type="submit"
							className="btn hc-bg-primary text-white px-5 py-2 rounded-3 fw-semibold shadow"
							disabled={formik.isSubmitting}
						>
							{formik.isSubmitting ? (
								<>
									<span
										className="spinner-border spinner-border-sm me-2"
										role="status"
										aria-hidden="true"
									/>
									Saving...
								</>
							) : (
								"Save"
							)}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}